/**
 * ---------------------------------------------------------------------------------------------------------------------
 * KC CART — v28: a freebie the shopper removes stays removed
 * ---------------------------------------------------------------------------------------------------------------------
 * Until now the rewards engine re-added the free sample on the very next render whenever a tier was unlocked and
 * nothing had been chosen. A shopper who deliberately took the gift out — to make room for another offer, or because
 * they did not want it — watched it reappear, which reads as the cart arguing with them.
 *
 * Removing a freebie is now recorded on the cart itself, in the `_kc_declined` cart attribute, as a comma-separated
 * list of goal keys ("goal2,goal3"). snippets/kc-rewards.liquid reads that attribute and skips auto-add for those
 * goals, so the decision survives page loads and is made server side like every other qualification decision.
 *
 * The note is written BEFORE the line is removed, so the section re-render that follows already knows about it.
 * That ordering is why this file takes the click over rather than letting kc-cart-v27.js handle it: listeners on
 * `window` run ahead of listeners on `document` in the capture phase.
 *
 * The note is cleared when the shopper picks a sample for that tier themselves, and when the cart is emptied —
 * an empty cart is a fresh start.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__kcCartV28) return;
  window.__kcCartV28 = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ATTR = '_kc_declined';

  /* ------------------------------------------------------------------------------------------------------------------
   * The Prestige cart contract, same as kc-cart-v27.js uses
   * --------------------------------------------------------------------------------------------------------------- */

  function post(endpoint, payload) {
    return fetch(ROOT + endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json();
    });
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function announce(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cart } })
    );
  }

  function loading(state) {
    document.documentElement.dispatchEvent(
      new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  function changeLine(payload) {
    payload.sections = bundledSections().join(',');
    return post('cart/change.js', payload).then(function (cart) {
      announce(cart);
      return cart;
    });
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * The declined list
   * --------------------------------------------------------------------------------------------------------------- */

  // kc-rewards.liquid prints the current value onto the rewards root, so the DOM is the source of truth between
  // renders. The cache only covers the moment after a write and before the re-render lands.
  var cached = null;

  function declined() {
    var root = document.querySelector('[data-kc-root]');
    var raw = root ? root.getAttribute('data-kc-declined') : null;
    if (raw === null) return cached ? cached.slice() : [];
    return raw
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function writeDeclined(list) {
    cached = list.slice();
    var value = list.join(',');
    var root = document.querySelector('[data-kc-root]');
    if (root) root.setAttribute('data-kc-declined', value);
    var attributes = {};
    attributes[ATTR] = value;
    return post('cart/update.js', { attributes: attributes });
  }

  function decline(tier) {
    if (!tier) return Promise.resolve();
    var list = declined();
    if (list.indexOf(tier) !== -1) return Promise.resolve();
    list.push(tier);
    return writeDeclined(list);
  }

  function undecline(tier) {
    if (!tier) return Promise.resolve();
    var list = declined();
    var at = list.indexOf(tier);
    if (at === -1) return Promise.resolve();
    list.splice(at, 1);
    return writeDeclined(list);
  }

  function tierOf(element) {
    var card = element.closest('[data-kc-tier]');
    if (card) return card.getAttribute('data-kc-tier');
    var row = element.closest('[data-kc-gift-line]');
    return row ? row.getAttribute('data-kc-gift-line') : '';
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Click handling — on `window`, so it runs ahead of the handlers kc-cart-v27.js and kc-cart-drawer.js put on
   * `document`. Only freebie clicks are taken over; everything else falls through untouched.
   * --------------------------------------------------------------------------------------------------------------- */

  window.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var target = event.target;
      if (!target || !target.closest) return;

      // 1. The picker's own "remove" control.
      var removeButton = target.closest('[data-kc-remove-gift]');
      if (removeButton) {
        var key = removeButton.getAttribute('data-kc-line-key');
        if (!key) return;

        event.preventDefault();
        event.stopPropagation();

        var tier = tierOf(removeButton);
        removeButton.setAttribute('aria-busy', 'true');
        loading(true);
        decline(tier)
          .then(function () {
            return changeLine({ id: key, quantity: 0 });
          })
          .catch(function () {
            /* the cart did not move; the note is harmless on its own */
          })
          .then(function () {
            loading(false);
            removeButton.removeAttribute('aria-busy');
          });
        return;
      }

      // 2. The trash icon on a freebie's cart row.
      var cartLink = target.closest('a[href]');
      if (cartLink && /\/cart\/change/.test(cartLink.getAttribute('href') || '')) {
        var row = cartLink.closest('[data-kc-gift-line]');
        if (!row) return;

        var url;
        try {
          url = new URL(cartLink.href, window.location.origin);
        } catch (e) {
          return;
        }

        var quantity = parseInt(url.searchParams.get('quantity'), 10);
        if (isNaN(quantity) || quantity !== 0) return;

        var id = cartLink.getAttribute('data-kc-line-key') || url.searchParams.get('id');
        var line = url.searchParams.get('line');
        var payload = id ? { id: id, quantity: 0 } : line ? { line: parseInt(line, 10), quantity: 0 } : null;
        if (!payload) return;

        event.preventDefault();
        event.stopPropagation();

        row.classList.add('is-kc-removing');
        loading(true);
        decline(row.getAttribute('data-kc-gift-line'))
          .then(function () {
            return changeLine(payload);
          })
          .catch(function () {
            row.classList.remove('is-kc-removing');
          })
          .then(function () {
            loading(false);
          });
        return;
      }

      // 3. Choosing a sample is the shopper changing their mind back.
      var addButton = target.closest('[data-kc-add-gift]');
      if (addButton) undecline(tierOf(addButton));
    },
    true
  );

  /* An emptied cart starts over. */
  document.documentElement.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    if (!cart || cart.item_count !== 0) return;
    if (!cart.attributes || !cart.attributes[ATTR]) return;
    writeDeclined([]);
  });
})();
